import React, { createContext, useContext, useState, useEffect } from "react";
import {
  ThemeProvider as MuiThemeProvider,
  createTheme,
} from "@mui/material/styles";

export type ThemeName = "classic" | "modern" | "dark";

export interface ThemeConfig {
  name: ThemeName;
  // Board styling
  board: {
    background: string;
    border: string;
    borderRadius: string;
    boxShadow: string;
    beforeBackground?: string;
  };
  // Tile styling
  tile: {
    background: string;
    border: string;
    boxShadow: string;
    hoverBorder: string;
    hoverBoxShadow: string;
    winningBackground: string;
    winningBorder: string;
  };
  // Dialog styling
  dialog: {
    background: string;
    border: string;
    headerBackground: string;
    headerBorder: string;
  };
  // AppBar styling
  appBar: {
    background: string;
  };
  // Accent colors (for highlights, current turn, winner, etc.)
  accent: {
    primary: string; // Main accent color
    light: string; // Light variant
    glow: string; // For glowing effects
    background: string; // For backgrounds
    border: string; // For borders
  };
}

interface ThemeContextType {
  themeName: ThemeName;
  setThemeName: (theme: ThemeName) => void;
  themeConfig: ThemeConfig;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
};

// Classic/Retro Theme - Warm wood with golden accents
const classicTheme = createTheme({
  palette: {
    mode: "dark",
    primary: {
      main: "#D4AF37", // Gold
      contrastText: "#1c1410",
    },
    secondary: {
      main: "#4A3728", // Wood brown
      contrastText: "#D4AF37",
    },
    background: {
      default: "#1c1410",
      paper: "#2c2416",
    },
    text: {
      primary: "#ffffff",
      secondary: "rgba(212, 175, 55, 0.7)",
    },
    error: {
      main: "#d32f2f",
    },
    warning: {
      main: "#ffa726",
    },
    success: {
      main: "#66bb6a",
    },
    divider: "rgba(212, 175, 55, 0.3)",
  },
  components: {
    MuiButton: {
      styleOverrides: {
        contained: {
          backgroundColor: "#D4AF37",
          color: "#1c1410",
          "&:hover": {
            backgroundColor: "#C4A037",
          },
        },
      },
    },
  },
});

// Modern Theme - Light glass/chrome aesthetic
const modernTheme = createTheme({
  palette: {
    mode: "light",
    primary: {
      main: "#90A4AE", // Silver blue
      contrastText: "#ffffff",
    },
    secondary: {
      main: "#ECEFF1", // Very light gray
      contrastText: "#37474F",
    },
    background: {
      default: "#F5F7FA",
      paper: "#FFFFFF",
    },
    text: {
      primary: "#263238",
      secondary: "#607D8B",
    },
    error: {
      main: "#f44336",
    },
    warning: {
      main: "#ff9800",
    },
    success: {
      main: "#4caf50",
    },
    divider: "rgba(96, 125, 139, 0.15)",
  },
  components: {
    MuiButton: {
      styleOverrides: {
        contained: {
          backgroundColor: "#90A4AE",
          color: "#ffffff",
          "&:hover": {
            backgroundColor: "#78909C",
          },
        },
      },
    },
  },
});

// Dark Theme - Dark cyberpunk with cyan/neon accents
const darkTheme = createTheme({
  palette: {
    mode: "dark",
    primary: {
      main: "#00BCD4", // Cyan neon
      contrastText: "#ffffff",
    },
    secondary: {
      main: "#1a1a1a", // Very dark gray
      contrastText: "#00BCD4",
    },
    background: {
      default: "#0a0a0a",
      paper: "#1a1a1a",
    },
    text: {
      primary: "#ffffff",
      secondary: "rgba(0, 188, 212, 0.7)",
    },
    error: {
      main: "#f44336",
    },
    warning: {
      main: "#ff9800",
    },
    success: {
      main: "#4caf50",
    },
    divider: "rgba(0, 188, 212, 0.15)",
  },
  components: {
    MuiButton: {
      styleOverrides: {
        contained: {
          backgroundColor: "#00BCD4",
          color: "#ffffff",
          "&:hover": {
            backgroundColor: "#00ACC1",
          },
        },
      },
    },
  },
});

const themes = {
  classic: classicTheme,
  modern: modernTheme,
  dark: darkTheme,
};

// Theme-specific configurations for Board, Tiles, Dialogs
const themeConfigs: Record<ThemeName, ThemeConfig> = {
  classic: {
    name: "classic",
    board: {
      background:
        "linear-gradient(145deg, #8B7355 0%, #6B5644 50%, #5B4634 100%)",
      border: "8px solid #4A3728",
      borderRadius: "16px",
      boxShadow: `
        inset 0 2px 4px rgba(0,0,0,0.3),
        inset 0 -2px 4px rgba(255,255,255,0.1),
        0 8px 16px rgba(0,0,0,0.3)
      `,
      beforeBackground: "linear-gradient(145deg, #5B4634, #3A2818)",
    },
    tile: {
      background: "rgba(0,0,0,0.2)",
      border: "2px solid rgba(74,55,40,0.3)",
      boxShadow: `
        inset 0 2px 6px rgba(0,0,0,0.5),
        0 2px 4px rgba(0,0,0,0.3)
      `,
      hoverBorder: "3px solid #D4AF37",
      hoverBoxShadow: `
        0 0 20px rgba(212, 175, 55, 0.6),
        0 0 40px rgba(212, 175, 55, 0.3),
        inset 0 2px 6px rgba(0,0,0,0.25)
      `,
      winningBackground: "linear-gradient(145deg, #FFD700, #D4AF37)",
      winningBorder: "3px solid #D4AF37",
    },
    dialog: {
      background: "linear-gradient(145deg, #2c2416, #1c1410)",
      border: "4px solid #4A3728",
      headerBackground: "rgba(212, 175, 55, 0.05)",
      headerBorder: "2px solid rgba(212, 175, 55, 0.3)",
    },
    appBar: {
      background: "#2c2416",
    },
    accent: {
      primary: "#D4AF37",
      light: "rgba(255, 215, 0, 0.15)",
      glow: "rgba(212, 175, 55, 0.6)",
      background: "rgba(212, 175, 55, 0.1)",
      border: "rgba(212, 175, 55, 0.4)",
    },
  },
  modern: {
    name: "modern",
    board: {
      background:
        "linear-gradient(145deg, rgba(255,255,255,0.95) 0%, rgba(245,247,250,0.98) 100%)",
      border: "4px solid rgba(144,164,174,0.3)",
      borderRadius: "20px",
      boxShadow: `
        inset 0 2px 12px rgba(255,255,255,0.8),
        inset 0 -2px 8px rgba(96,125,139,0.1),
        0 8px 32px rgba(96,125,139,0.15),
        0 2px 8px rgba(0,0,0,0.05)
      `,
      beforeBackground:
        "linear-gradient(145deg, rgba(236,239,241,0.5), rgba(255,255,255,0.3))",
    },
    tile: {
      background: "rgba(96,125,139,0.15)",
      border: "2px solid rgba(96,125,139,0.2)",
      boxShadow: `
        inset 0 2px 6px rgba(96,125,139,0.1),
        0 2px 8px rgba(96,125,139,0.1)
      `,
      hoverBorder: "3px solid #90A4AE",
      hoverBoxShadow: `
        0 0 20px rgba(144,164,174,0.4),
        0 0 40px rgba(144,164,174,0.2),
        inset 0 2px 8px rgba(144,164,174,0.2)
      `,
      winningBackground:
        "linear-gradient(145deg, rgba(144,164,174,0.3), rgba(120,144,156,0.3))",
      winningBorder: "3px solid #78909C",
    },
    dialog: {
      background: "linear-gradient(145deg, #FFFFFF, #F5F7FA)",
      border: "3px solid rgba(144,164,174,0.25)",
      headerBackground: "rgba(144, 164, 174, 0.08)",
      headerBorder: "2px solid rgba(144, 164, 174, 0.2)",
    },
    appBar: {
      background: "linear-gradient(135deg, rgba(144, 164, 174, 0.08) 0%, rgba(144, 164, 174, 0.12) 100%)",
    },
    accent: {
      primary: "#90A4AE",
      light: "rgba(144, 164, 174, 0.15)",
      glow: "rgba(144, 164, 174, 0.4)",
      background: "rgba(144, 164, 174, 0.1)",
      border: "rgba(144, 164, 174, 0.3)",
    },
  },
  dark: {
    name: "dark",
    board: {
      background:
        "linear-gradient(145deg, rgba(20,20,20,0.98) 0%, rgba(10,10,10,1) 100%)",
      border: "6px solid rgba(0,188,212,0.3)",
      borderRadius: "20px",
      boxShadow: `
        inset 0 2px 8px rgba(0,0,0,0.8),
        inset 0 -2px 8px rgba(0,188,212,0.1),
        0 0 50px rgba(0,188,212,0.25),
        0 12px 40px rgba(0,0,0,0.6)
      `,
      beforeBackground:
        "linear-gradient(145deg, rgba(0,188,212,0.08), rgba(0,150,180,0.04))",
    },
    tile: {
      background:
        "linear-gradient(145deg, rgba(30,30,30,0.9), rgba(20,20,20,0.95))",
      border: "2px solid rgba(0,188,212,0.25)",
      boxShadow: `
        inset 0 2px 8px rgba(0,188,212,0.08),
        inset 0 -2px 8px rgba(0,0,0,0.5),
        0 2px 12px rgba(0,0,0,0.4)
      `,
      hoverBorder: "3px solid #00BCD4",
      hoverBoxShadow: `
        0 0 30px rgba(0,188,212,0.7),
        0 0 60px rgba(0,188,212,0.4),
        inset 0 2px 8px rgba(0,188,212,0.2)
      `,
      winningBackground:
        "linear-gradient(145deg, rgba(0,229,255,0.35), rgba(0,188,212,0.35))",
      winningBorder: "3px solid #00E5FF",
    },
    dialog: {
      background: "linear-gradient(145deg, #1a1a1a, #0a0a0a)",
      border: "3px solid rgba(0,188,212,0.25)",
      headerBackground: "rgba(0, 188, 212, 0.1)",
      headerBorder: "2px solid rgba(0, 188, 212, 0.25)",
    },
    appBar: {
      background: "#1a1a1a",
    },
    accent: {
      primary: "#00BCD4",
      light: "rgba(0, 188, 212, 0.15)",
      glow: "rgba(0, 188, 212, 0.6)",
      background: "rgba(0, 188, 212, 0.1)",
      border: "rgba(0, 188, 212, 0.4)",
    },
  },
};

interface ThemeProviderProps {
  children: React.ReactNode;
}

export function ThemeProvider({ children }: ThemeProviderProps) {
  const [themeName, setThemeNameState] = useState<ThemeName>("classic");

  // Load theme from localStorage on mount
  useEffect(() => {
    const savedSettings = localStorage.getItem("personalSettings");
    if (savedSettings) {
      try {
        const settings = JSON.parse(savedSettings);
        if (settings.theme && themes[settings.theme as ThemeName]) {
          setThemeNameState(settings.theme as ThemeName);
        }
      } catch (error) {
        console.error("Error loading theme from localStorage:", error);
      }
    }
  }, []);

  // Update CSS variables when theme changes
  useEffect(() => {
    const currentTheme = themes[themeName];
    const root = document.documentElement;

    root.style.setProperty(
      "--color-primary",
      currentTheme.palette.primary.main
    );
    root.style.setProperty(
      "--color-primary-light",
      currentTheme.palette.primary.light || currentTheme.palette.primary.main
    );
    root.style.setProperty(
      "--color-primary-dark",
      currentTheme.palette.primary.dark || currentTheme.palette.primary.main
    );
    root.style.setProperty(
      "--color-primary-contrast",
      currentTheme.palette.primary.contrastText
    );

    root.style.setProperty(
      "--color-secondary",
      currentTheme.palette.secondary.main
    );
    root.style.setProperty(
      "--color-secondary-light",
      currentTheme.palette.secondary.light ||
        currentTheme.palette.secondary.main
    );
    root.style.setProperty(
      "--color-secondary-dark",
      currentTheme.palette.secondary.dark || currentTheme.palette.secondary.main
    );
    root.style.setProperty(
      "--color-secondary-contrast",
      currentTheme.palette.secondary.contrastText
    );

    root.style.setProperty(
      "--color-background-default",
      currentTheme.palette.background.default
    );
    root.style.setProperty(
      "--color-background-paper",
      currentTheme.palette.background.paper
    );

    root.style.setProperty(
      "--color-text-primary",
      currentTheme.palette.text.primary
    );
    root.style.setProperty(
      "--color-text-secondary",
      currentTheme.palette.text.secondary
    );

    root.style.setProperty("--color-divider", currentTheme.palette.divider);
    root.style.setProperty("--color-error", currentTheme.palette.error.main);
    root.style.setProperty(
      "--color-warning",
      currentTheme.palette.warning.main
    );
    root.style.setProperty(
      "--color-success",
      currentTheme.palette.success.main
    );
  }, [themeName]);

  const setThemeName = (newTheme: ThemeName) => {
    setThemeNameState(newTheme);
    // Save to localStorage
    const savedSettings = localStorage.getItem("personalSettings");
    let settings = { theme: newTheme };
    if (savedSettings) {
      try {
        settings = { ...JSON.parse(savedSettings), theme: newTheme };
      } catch (error) {
        console.error("Error parsing saved settings:", error);
      }
    }
    localStorage.setItem("personalSettings", JSON.stringify(settings));
  };

  const currentTheme = themes[themeName];
  const themeConfig = themeConfigs[themeName];

  return (
    <ThemeContext.Provider value={{ themeName, setThemeName, themeConfig }}>
      <MuiThemeProvider theme={currentTheme}>{children}</MuiThemeProvider>
    </ThemeContext.Provider>
  );
}
